import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { HELP_ANCHORS, helpAnchor } from "@keydials/ui";
import { SECTION_EDGE_FRACTION, fitTextToEms, sectionBandFill, sectionLabelColour, splitEdgeArc } from "@keydials/dial-renderer";
import { sectionTitle, type DialVM, type VSection, type VSegment } from "../dialModel";
import { RAG_COLOUR } from "../lib/ui";
import { uprightAfter } from "../workspace/dialRotation";
import { labelArc, polar, wedge } from "./dialGeometry";
import { Icon } from "./Icon";

const C = 50; // centre of the 0..100 viewBox

/** How long the centre logo takes to come back upright once the ring has landed. Deliberately slower than
 * the turn it just rode: the way out is the dial's movement and takes the dial's pace, the way back is the
 * logo's own, and at the ring's speed it reads as a second turn rather than as settling. */
const HUB_SETTLE_MS = 1400;
/** The ring's easing, so the outward ride is indistinguishable from the thing it is riding. */
const RIDE_EASE = "cubic-bezier(.4,0,.2,1)";
/** Ease-out for the way back: it leaves at once and glides into upright, rather than starting with a lurch. */
const SETTLE_EASE = "cubic-bezier(.22,1,.36,1)";

/** Fit a label to the arc available to it: the full text, then an ellipsis, then nothing as it shrinks
 * (the full label is still exposed via the section's hover title + aria-label). */
function fitToArc(label: string, arcLength: number, fontSize: number): string {
  // Measured and cut a whole character at a time, so an emoji counts as one (wide) character and is never split
  // in half (KEY-757). Plain text fits exactly as it always has: ~0.6em a character.
  return fitTextToEms(label, (arcLength * 0.9) / fontSize);
}

interface Hover {
  segment: VSegment;
}

function Tooltip({ hover, x, y, drillIn }: { hover: Hover; x: number; y: number; drillIn?: boolean }) {
  // Per-segment, not per-dial: an itemised action point with no items yet carries its own status (KEY-259),
  // so an item tally would read a meaningless "0 / 0".
  const detail = !hover.segment.itemActive
    ? hover.segment.status?.name ?? "Not set"
    : `${hover.segment.done} / ${hover.segment.total} items complete`;
  return (
    <div
      style={{
        position: "absolute",
        left: x,
        top: y,
        transform: "translate(-50%,-128%)",
        background: "var(--surface)",
        color: "var(--text)",
        border: "var(--border-w) solid var(--border)",
        borderRadius: "var(--radius-sm)",
        boxShadow: "var(--shadow-md)",
        padding: "6px 10px",
        fontSize: 12.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
        pointerEvents: "none",
        zIndex: 50,
      }}
    >
      {hover.segment.name}
      {/* KEY-533: on a group, clicking a wedge LEAVES this dial. Saying so on hover is the only warning
          there is — the wedge looks exactly like one that would open a panel beside the dial. */}
      <div style={{ fontSize: 11, fontWeight: 500, color: "var(--text-muted)", marginTop: 1 }}>
        {detail}{drillIn ? " · Open dial →" : ""}
      </div>
    </div>
  );
}

/** Thin completion arc hugging the inner edge of the segments, sweeping clockwise from 12 o'clock. */
function CompletionRing({ segIn, pct }: { segIn: number; pct: number }) {
  const ringOut = segIn - 1.3;
  const ringIn = ringOut - 1.2;
  const r = (ringOut + ringIn) / 2;
  const w = ringOut - ringIn;
  const circ = 2 * Math.PI * r;
  return (
    <g style={{ pointerEvents: "none" }}>
      {/* KEY-755: track and fill read the theme's --dial-* tokens, with today's colours as the fallback. */}
      <circle cx={C} cy={C} r={r} fill="none" strokeWidth={w} style={{ stroke: "var(--dial-track, #dfe3ec)" }} />
      {pct > 0 && (
        <circle
          cx={C}
          cy={C}
          r={r}
          fill="none"
          strokeWidth={w}
          strokeLinecap="round"
          strokeDasharray={`${(circ * pct).toFixed(3)} ${(circ * (1 - pct) + circ).toFixed(3)}`}
          transform={`rotate(-90 ${C} ${C})`}
          style={{ stroke: `var(--dial-complete, ${RAG_COLOUR.Green})`, transition: "stroke-dasharray .55s ease" }}
        />
      )}
    </g>
  );
}

export interface DialViewProps {
  vm: DialVM;
  /** Show the central completion ring. */
  progress?: boolean;
  /** Enable hover tooltips + click/keyboard selection. */
  interactive?: boolean;
  /** Render curved section labels (off for thumbnails). */
  showLabels?: boolean;
  selectedSectionKey?: string | null;
  selectedSegmentKey?: string | null;
  /** KEY-532: clockwise degrees to turn the ring, so the selection sits at East. CUMULATIVE — callers add a
   * shortest-way delta rather than resetting to 0, or the dial unwinds the long way round on every pick.
   * 0 (the default, and all of Classic mode) emits no transform at all. */
  rotationDeg?: number;
  /** How long that turn takes. 0 snaps, which is what `prefers-reduced-motion` and the first paint want. */
  rotationMs?: number;
  /** KEY-533: draw the group cues — the hub chip, a hint inside each wedge that it is itself a dial, and the
   * hover lift. (The stacked discs the handoff described were never drawn.) Off by default, and off for a
   * thumbnail: at 104px none of it resolves. */
  groupCues?: boolean;
  onSelectSection?: (sectionKey: string) => void;
  /** segmentKey === null signals "deselect" (clicking the already-selected segment). */
  onSelectSegment?: (sectionKey: string, segmentKey: string | null) => void;
  /** When set (and interactive), the centre hub becomes a button that invokes this — a dial admin clicking
   * the logo to change it. Omit to render a passive hub. */
  onEditCentreLogo?: () => void;
  /** KEY-640: for a passive hub, a hover note saying why it isn't clickable — shown to a dial admin whose dial
   * doesn't have the logo change switched on. A tooltip only: the hub stays passive (no button role, not
   * focusable). Ignored when `onEditCentreLogo` makes the hub a button. */
  centreLogoNote?: string;
  /**
   * Look back's Then dial. It changes only what sits BEHIND the ring — an inset disc with a dashed rim — and
   * drops the hover tooltip, so it reads as a record rather than as the dial you work on. It never touches a
   * fill: the colours are the data, and a past face drawn in faded colours would misreport what the statuses
   * were. Still selectable, so picking on either dial moves both.
   */
  variant?: "then";
  /** Overrides the accessible name, e.g. "Then dial, as it was on 29 Jun 2026". */
  ariaLabel?: string;
}

interface PlacedSegment {
  section: VSection;
  segment: VSegment;
  a0: number;
  a1: number;
}

/** The Key Dials visualization: navy section ring + status/RAG segment ring around a KD hub. */
export function DialView({
  vm,
  progress = false,
  interactive = true,
  showLabels = true,
  selectedSectionKey,
  selectedSegmentKey,
  rotationDeg = 0,
  rotationMs = 0,
  groupCues = false,
  onSelectSection,
  onSelectSegment,
  onEditCentreLogo,
  centreLogoNote,
  variant,
  ariaLabel,
}: DialViewProps) {
  const then = variant === "then";
  const [hover, setHover] = useState<Hover | null>(null);
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  const [hubHover, setHubHover] = useState(false);

  // The centre logo rides the ring's turn and then unwinds to upright (see the hub itself, below). Held as
  // state rather than derived from `rotationDeg`, because the two halves of the move are a sequence in TIME,
  // not a function of the angle: one rotation means "travelling" for the length of the turn and "settling"
  // for a while after it.
  const [hub, setHub] = useState<{ deg: number; ms: number; settling: boolean }>({ deg: 0, ms: 0, settling: false });
  // Where the logo was last sent, and where the ring was when we sent it. The logo follows the ring's DELTA
  // rather than its absolute angle — it starts each turn from upright, so the two agree on how far to go but
  // never on where to stop.
  const hubTargetRef = useRef(0);
  const ringDegRef = useRef(rotationDeg);

  useEffect(() => {
    const delta = rotationDeg - ringDegRef.current;
    ringDegRef.current = rotationDeg;
    if (delta === 0) {
      return;
    }
    if (rotationMs <= 0) {
      // Nothing to ride: the ring arrives without travelling. That is `prefers-reduced-motion`, the first
      // paint, and the whole of Classic mode — in every one of them the logo simply stays upright.
      hubTargetRef.current = 0;
      setHub({ deg: 0, ms: 0, settling: false });
      return;
    }
    const travelled = hubTargetRef.current + delta;
    hubTargetRef.current = travelled;
    setHub({ deg: travelled, ms: rotationMs, settling: false });
    // The turn is a CSS transition, so nothing fires when it lands — this is the clock that says it has.
    // Keyed on the rotation itself, so a pick made mid-turn re-aims the logo instead of letting the first
    // timer start unwinding it while the ring is still moving.
    const settle = setTimeout(() => {
      hubTargetRef.current = uprightAfter(travelled);
      setHub({ deg: hubTargetRef.current, ms: HUB_SETTLE_MS, settling: true });
    }, rotationMs);
    return () => clearTimeout(settle);
  }, [rotationDeg, rotationMs]);

  // Geometry (SVG units from centre) — matches the handoff dial.
  const SL_OUT = 49.4;
  const SL_IN = 39.6;
  const AR_OUT = 38.8;
  const AR_IN = progress ? 22.0 : 19.5;
  const HOLE = progress ? 16.8 : 18.6;
  const labelR = (SL_IN + SL_OUT) / 2;

  const sections = vm.sections.filter((s) => s.segments.length > 0);
  const totalSegments = sections.reduce((n, s) => n + s.segments.length, 0);
  if (totalSegments === 0) {
    return null;
  }

  // Equal angular slice per section, starting at 12 o'clock and sweeping clockwise.
  const secAngle = (2 * Math.PI) / sections.length;
  const secRange = new Map<string, { a0: number; a1: number }>();
  let cur = -Math.PI / 2;
  for (const s of sections) {
    secRange.set(s.key, { a0: cur, a1: cur + secAngle });
    cur += secAngle;
  }
  const sectionArcLen = secAngle * labelR; // arc length available to each section's curved label

  // Subdivide each section equally among its segments.
  const placed: PlacedSegment[] = [];
  for (const section of sections) {
    const range = secRange.get(section.key)!;
    const step = secAngle / section.segments.length;
    let a = range.a0;
    for (const segment of section.segments) {
      placed.push({ section, segment, a0: a, a1: a + step });
      a += step;
    }
  }

  // KEY-513: a section counts as a selection too. Picking one used to leave every band and wedge at full
  // opacity, so the dial gave no sign the level had changed — only an action point dimmed anything.
  const selecting = !!selectedSegmentKey || !!selectedSectionKey;
  const imageUrl = vm.detail.centralImageUrl;

  // KEY-532. Zero is the whole of Classic mode: no transform is emitted at all, so the dial renders exactly
  // as it always has. The hub still sits OUTSIDE the rotating group, but it no longer sits STILL: it rides
  // the turn on a transform of its own and then unwinds to upright once the ring has landed. That sequence
  // is what answers the two objections to simply putting the logo inside the group — it never keeps turning
  // after the dial has stopped (which would read as a loading spinner), and it never comes to REST upside
  // down, because coming to rest is precisely coming back upright.
  // Distinct CHILD DIALS, not wedges: several wedges can map sections of the same child (KEY-153), and
  // "Group · 12 dials" over four dials would be a lie.
  const childCount = groupCues
    ? new Set(
        vm.sections
          .flatMap((sec) => sec.segments)
          .map((sg) => sg.childInstanceId)
          .filter((id): id is string => !!id),
      ).size
    : 0;

  const rotationRad = (rotationDeg * Math.PI) / 180;
  const rotating = rotationDeg !== 0 || rotationMs > 0;
  const ringTransform = rotating
    ? { transform: `rotate(${rotationDeg}deg)`, transformOrigin: "50px 50px", transition: rotationMs > 0 ? `transform ${rotationMs}ms ${RIDE_EASE}` : undefined }
    : undefined;
  // Separate from the ring's so the ride out and the way back can run at different speeds, and omitted
  // entirely while the logo is upright and has never moved — Classic mode and every thumbnail then emit the
  // same SVG they always did.
  const hubTransform = hub.deg !== 0 || hub.ms > 0
    ? {
        transform: `rotate(${hub.deg}deg)`,
        transformOrigin: "50px 50px",
        transition: hub.ms > 0 ? `transform ${hub.ms}ms ${hub.settling ? SETTLE_EASE : RIDE_EASE}` : undefined,
      }
    : undefined;

  const moveTip = (e: ReactMouseEvent<SVGElement>) => {
    const svg = e.currentTarget.ownerSVGElement;
    if (!svg) {
      return;
    }
    const rect = svg.getBoundingClientRect();
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }} data-variant={variant}>
      {then && (
        // Behind the ring, never over it: the disc is the only thing that says "Then", so the wedges keep
        // exactly the colours the replayed statuses give them.
        <div
          aria-hidden="true"
          style={{
            position: "absolute", inset: "-3%", borderRadius: "50%",
            background: "var(--surface-inset)", border: "1px dashed var(--text-faint)", pointerEvents: "none",
          }}
        />
      )}
      <svg
        viewBox="0 0 100 100"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block", overflow: "visible", position: "relative" }}
        role="img"
        aria-label={ariaLabel ?? `${vm.detail.name} dial — ${Math.round(vm.completion * 100)}% complete`}
        // The dial is the navigation, and that is the first thing the first-run tour says (KEY-535). Only
        // when it is interactive: the dashboard's cards, the group tiles and the login hero all render this
        // same component, and a tour must spotlight the dial you can actually steer, not a thumbnail.
        {...(interactive && !then ? helpAnchor(HELP_ANCHORS.dialFace) : {})}
      >
        {showLabels && (
          <defs>
            {sections.map((s) => {
              const r = secRange.get(s.key)!;
              const mid = (r.a0 + r.a1) / 2;
              // Which way round the text sits is a fact about where the section ENDS UP, not where it was
              // authored: read from the un-rotated angle a rotated section's label would hang upside down
              // for half of every turn.
              const rev = Math.sin(mid + rotationRad) > 0;
              const off = 0.8;
              const lr = rev ? labelR + off : labelR - off;
              return <path key={`lp-${s.key}`} id={`kd-lp-${s.key}`} d={labelArc(C, C, r.a0, r.a1, lr, rev)} fill="none" />;
            })}
          </defs>
        )}

        {/* Everything that turns (KEY-532): bands, labels, section edges and segments. One group, one
            transform — rotating them separately would let them drift apart mid-turn. */}
        <g style={ringTransform}>
        {/* Outer ring — section bands: the structural navy, or the section's own colour (KEY-755). */}
        {sections.map((s) => {
          const r = secRange.get(s.key)!;
          const muted = selecting && selectedSectionKey !== s.key;
          // The ring shows the section's mnemonic when set (short codes fit the tight band); the full
          // label stays available via the hover <title> and aria-label below.
          const labelText = fitToArc(sectionTitle(s), sectionArcLen, 2.9);
          return (
            <g key={s.key}>
              <title>{s.status ? `${s.label} — ${s.status.name}` : s.label}</title>
              <path
                d={wedge(C, C, r.a0, r.a1, SL_IN, SL_OUT)}
                data-section-band={s.key}
                fill={sectionBandFill(s.colour, "var(--ring-section)")}
                stroke="var(--surface)"
                strokeWidth={0.5}
                strokeLinejoin="round"
                opacity={muted ? 0.32 : 1}
                style={{ cursor: interactive && onSelectSection ? "pointer" : "default", transition: "opacity .35s ease", outline: "none" }}
                tabIndex={interactive && onSelectSection ? 0 : -1}
                role={interactive && onSelectSection ? "button" : undefined}
                aria-label={`Section ${s.label}`}
                onClick={interactive && onSelectSection ? () => onSelectSection(s.key) : undefined}
                onKeyDown={
                  interactive && onSelectSection
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelectSection(s.key);
                        }
                      }
                    : undefined
                }
              />
              {/* The section's own status (KEY-479): a coloured edge along the OUTSIDE of the band, never a
                  fill — eight filled sections would drown the action points that are the point of the dial.
                  Same depth as the static export, so the PDF and the screen agree.
                  KEY-494: when the dial splits the edge, the same ring is cut into one arc per action
                  point, each carrying that action point's own worst-of on the section's axis. */}
              {s.statusSegments
                ? s.statusSegments.map((arc) => {
                    const i = s.segments.findIndex((sg) => sg.key === arc.segmentKey);
                    if (i < 0 || !arc.colour) {
                      return null;
                    }
                    // Subdivided exactly as the action points below are, so each arc sits over its own.
                    const step = (r.a1 - r.a0) / s.segments.length;
                    const { startAngle, endAngle } = splitEdgeArc(r.a0 + i * step, r.a0 + (i + 1) * step);
                    return (
                      <path
                        key={arc.segmentKey}
                        d={wedge(C, C, startAngle, endAngle, SL_OUT - (SL_OUT - SL_IN) * SECTION_EDGE_FRACTION, SL_OUT)}
                        fill={arc.colour}
                        opacity={muted ? 0.32 : 1}
                        style={{ pointerEvents: "none", transition: "opacity .35s ease" }}
                        data-section-status-segment={arc.segmentKey}
                      />
                    );
                  })
                : s.status && (
                    <path
                      d={wedge(C, C, r.a0, r.a1, SL_OUT - (SL_OUT - SL_IN) * SECTION_EDGE_FRACTION, SL_OUT)}
                      fill={s.status.colour}
                      opacity={muted ? 0.32 : 1}
                      style={{ pointerEvents: "none", transition: "opacity .35s ease" }}
                      data-section-status={s.status.key}
                    />
                  )}
              {showLabels && labelText && (
                <text
                  fontSize={2.9}
                  fontWeight={600}
                  fill={sectionLabelColour(s.colour, "#fff")}
                  opacity={muted ? 0.5 : 1}
                  style={{ pointerEvents: "none", fontFamily: "var(--font-ui)", letterSpacing: "0.02px" }}
                >
                  <textPath href={`#kd-lp-${s.key}`} startOffset="50%" textAnchor="middle">
                    {labelText}
                  </textPath>
                </text>
              )}
            </g>
          );
        })}

        {/* Inner ring — segments (status colour for simple, RAG rollup for itemised). */}
        {placed.map(({ section, segment, a0, a1 }) => {
          const sel = selectedSegmentKey === segment.key && selectedSectionKey === section.key;
          // With a section but no action point picked, the whole of that section stays lit — its wedges are
          // what the panel beside it is describing.
          const inScope = selectedSegmentKey ? sel : selectedSectionKey === section.key;
          const muted = selecting && !inScope;
          return (
            <path
              key={`${section.key}/${segment.key}`}
              d={wedge(C, C, a0, a1, AR_IN, AR_OUT)}
              fill={segment.colour}
              stroke="var(--surface)"
              strokeWidth={sel ? 0.8 : 0.5}
              strokeLinejoin="round"
              opacity={muted ? 0.3 : groupCues && hover && hover.segment !== segment ? 0.72 : 1}
              style={{
                cursor: interactive && onSelectSegment ? "pointer" : "default",
                transition: "opacity .35s ease, transform .25s ease",
                outline: "none",
                // KEY-533: the hovered wedge lifts and the rest recede, so a group's segments read as
                // separate dials you are about to step into rather than as slices of this one.
                ...(groupCues && hover?.segment === segment
                  ? { transform: "scale(1.035)", transformOrigin: "50px 50px" }
                  : {}),
              }}
              tabIndex={interactive && onSelectSegment ? 0 : -1}
              role={interactive && onSelectSegment ? "button" : undefined}
              aria-label={`Segment ${segment.name}`}
              onMouseEnter={interactive && !then ? (e) => { setHover({ segment }); moveTip(e); } : undefined}
              onMouseMove={interactive && !then ? moveTip : undefined}
              onMouseLeave={interactive && !then ? () => { setHover(null); setTip(null); } : undefined}
              onClick={
                interactive && onSelectSegment
                  ? () => onSelectSegment(section.key, sel ? null : segment.key)
                  : undefined
              }
              onKeyDown={
                interactive && onSelectSegment
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelectSegment(section.key, sel ? null : segment.key);
                      }
                    }
                  : undefined
              }
            />
          );
        })}

        {/* KEY-533: a thin arc inside each wedge, hinting that the wedge is itself a dial. Inset 18% at each
            end so it reads as a ring within the segment rather than as a second edge butting its neighbours.
            Drawn as a shallow band rather than a stroked arc so it uses the same geometry as everything
            else here — a stroke would need its own path helper and its own rounding rules. */}
        {groupCues && placed.map(({ section, segment, a0, a1 }) => {
          const pad = (a1 - a0) * 0.18;
          const inScope = selectedSegmentKey
            ? selectedSegmentKey === segment.key && selectedSectionKey === section.key
            : selectedSectionKey === section.key;
          return (
            <path
              key={`mini-${section.key}/${segment.key}`}
              d={wedge(C, C, a0 + pad, a1 - pad, 33.3, 33.75)}
              fill="#fff"
              opacity={selecting && !inScope ? 0.2 : 0.7}
              style={{ pointerEvents: "none", transition: "opacity .35s ease" }}
            />
          );
        })}

        {progress && <CompletionRing segIn={AR_IN} pct={vm.completion} />}

        </g>

        {/* Centre hub: a white disc with the dial image, or the KD mark. When editable (a dial admin), the
            whole hub is a button that opens the logo editor, with a hover affordance. When it isn't, the
            host may still hand a note saying why, shown as a plain tooltip (KEY-640). */}
        {(() => {
          const editable = interactive && !!onEditCentreLogo;
          return (
            <g
              onClick={editable ? onEditCentreLogo : undefined}
              onMouseEnter={editable ? () => setHubHover(true) : undefined}
              onMouseLeave={editable ? () => setHubHover(false) : undefined}
              onKeyDown={
                editable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onEditCentreLogo!();
                      }
                    }
                  : undefined
              }
              tabIndex={editable ? 0 : -1}
              role={editable ? "button" : undefined}
              aria-label={editable ? "Change dial logo" : undefined}
              data-help-anchor={editable ? HELP_ANCHORS.dialLogo : undefined}
              style={{ cursor: editable ? "pointer" : "default", outline: "none" }}
            >
              {editable ? <title>Change dial logo</title> : interactive && centreLogoNote && <title>{centreLogoNote}</title>}
              {/* KEY-755: the hub disc and its rim are themed (--dial-hub / --dial-hub-border), today's white and
                  grey as the fallback. Dark keeps a white hub on purpose: dial logos are drawn for a light ground. */}
              <circle cx={C} cy={C} r={HOLE} style={{ fill: "var(--dial-hub, #ffffff)" }} />
              <circle cx={C} cy={C} r={HOLE} fill="none" strokeWidth={0.5} style={{ stroke: "var(--dial-hub-border, #e2e6ee)" }} />
              {/* Only the mark itself turns. The disc behind it is a circle either way, and the hover
                  affordance over it has to stay readable while the logo is mid-turn. */}
              <g data-dial-hub-logo="" style={hubTransform}>
                {imageUrl ? (
                  <>
                    <clipPath id={`kd-hub-${vm.detail.id}`}>
                      <circle cx={C} cy={C} r={HOLE * 0.9} />
                    </clipPath>
                    <image
                      href={imageUrl}
                      x={C - HOLE * 0.9}
                      y={C - HOLE * 0.9}
                      width={HOLE * 1.8}
                      height={HOLE * 1.8}
                      clipPath={`url(#kd-hub-${vm.detail.id})`}
                      preserveAspectRatio="xMidYMid slice"
                    />
                  </>
                ) : (
                  <KdHubMark r={HOLE} />
                )}
              </g>
              {editable && hubHover && (
                <g style={{ pointerEvents: "none" }}>
                  <circle cx={C} cy={C} r={HOLE * 0.9} fill="rgba(2,6,23,0.58)" />
                  <text
                    x={C}
                    y={C + 1}
                    textAnchor="middle"
                    fontSize={2.8}
                    fontWeight={700}
                    fill="#fff"
                    style={{ fontFamily: "var(--font-ui)" }}
                  >
                    Change logo
                  </text>
                </g>
              )}
            </g>
          );
        })()}
      </svg>

      {/* The chip sits just below the hub, naming what this dial is and how many dials are inside it.
          Inert: the hub behind it is a button for a dial admin, and a chip that swallowed that click would
          be a dead spot in the middle of the dial. --accent rather than the handoff's --navy, because navy
          resolves to WHITE in the dark theme — the chip would be unreadable. */}
      {groupCues && childCount > 0 && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: "65.5%",
            left: "50%",
            transform: "translateX(-50%)",
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            height: 24,
            padding: "0 10px",
            borderRadius: 999,
            background: "var(--accent)",
            color: "var(--on-accent)",
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          <Icon name="layers" size={12} />
          Group · {childCount} {childCount === 1 ? "dial" : "dials"}
        </div>
      )}

      {/* "Open dial →" only over a wedge that really opens one: an unlinked wedge, a scoped viewer's face and
          web-admin's draft preview all carry no child, and a click there opens a panel instead (KEY-572). */}
      {interactive && hover && tip && (
        <Tooltip hover={hover} x={tip.x} y={tip.y} drillIn={groupCues && !!hover.segment.childInstanceId} />
      )}
    </div>
  );
}

/** The KD mark sized for the dial hub. Inlined (not the shared KdMark) to live in dial coordinates. */
function KdHubMark({ r }: { r: number }) {
  const RING = ["#3eb034", "#f5a51c", "#f97316", "#e7161b", "#94a3b8"];
  const ringR = r * 0.66;
  const stroke = r * 0.12;
  const seg = (Math.PI * 2) / RING.length;
  const gap = seg * 0.18;
  const arc = (a0: number, a1: number) => {
    const [x0, y0] = polar(C, C, ringR, a0);
    const [x1, y1] = polar(C, C, ringR, a1);
    return `M ${x0} ${y0} A ${ringR} ${ringR} 0 0 1 ${x1} ${y1}`;
  };
  return (
    <g style={{ pointerEvents: "none" }}>
      {RING.map((colour, i) => {
        const a0 = -Math.PI / 2 + i * seg + gap / 2;
        const a1 = -Math.PI / 2 + (i + 1) * seg - gap / 2;
        return <path key={i} d={arc(a0, a1)} fill="none" stroke={colour} strokeWidth={stroke} strokeLinecap="round" />;
      })}
      <text
        x={C}
        y={C}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="var(--font-display)"
        fontWeight={800}
        fontSize={r * 0.6}
        fill="#1e1d51"
      >
        KD
      </text>
    </g>
  );
}
